import {
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import { useHistory, useLocation } from 'react-router-dom';
import InfoBox from '../../components/shared/InfoBox/InfoBox';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import {
  EvaluationTagDto,
  TemplateSummary,
  serviceEvaluations,
} from '../../services/service-evaluations';
import { EvaluationErrors } from '../../util/evaluationContract';
import {
  evaluationsUrl,
  fromOf,
  lastEvaluationsUrl,
  readWizardSeed,
} from '../../util/evaluationsNavigation';
import { toIsoDay } from '../../util/bidRequestReminder';
import { useLeaveGuard } from '../../util/useLeaveGuard';
import BtButton from '../bt-button/bt-button';
import BtStepper from '../bt-stepper/bt-stepper';
import MissingLine from '../app-evaluation-start/MissingLine';
import {
  IdempotencyKey,
  errorKeyOf,
  evaluationErrorMessage,
  hasGlobalNotice,
  idempotencyKeyFor,
  isUnansweredSend,
  outcomeToast,
} from '../app-evaluation-start/evaluationStart';
import { reportUnexpected } from '../page-supplier-evaluations/reportUnexpected';
import ContextStep from './ContextStep';
import LeaveWizardModal from './LeaveWizardModal';
import RecipientsStep from './RecipientsStep';
import ReviewStep from './ReviewStep';
import SuppliersStep from './SuppliersStep';
import TemplateStep, { TemplatesStatus } from './TemplateStep';
import {
  LAST_STEP,
  WizardData,
  effectiveTagIds,
  isDirty,
  missingAll,
  missingFor,
  missingText,
  seedToState,
  seededText,
  toWizardBody,
  wizardReducer,
} from './wizardState';
import styles from './app-evaluation-wizard.module.scss';

type Copy = [string, string];

const STEPS: { label: Copy; heading: Copy; intro: Copy }[] = [
  {
    label: ['wizard.steps.context', 'Kontext'],
    heading: ['wizard.context.heading', 'Kontext'],
    intro: [
      'wizard.context.intro',
      'Välj vad utvärderingen gäller: ett befintligt projekt, ett historiskt projekt eller inget projekt alls. Varken inköp eller vinnande leverantör krävs.',
    ],
  },
  {
    label: ['wizard.steps.suppliers', 'Leverantörer'],
    heading: ['wizard.suppliers.heading', 'Leverantörer'],
    intro: ['wizard.suppliers.intro', 'Välj leverantör som ska utvärderas.'],
  },
  {
    label: ['wizard.steps.template', 'Mall'],
    heading: ['wizard.template.heading', 'Mall'],
    intro: [
      'wizard.template.intro',
      'Frågorna sparas med utvärderingen när den skickas, så senare ändringar i mallen påverkar inte den här utvärderingen.',
    ],
  },
  {
    label: ['wizard.steps.recipients', 'Mottagare'],
    heading: ['wizard.recipients.heading', 'Mottagare och svarsdag'],
    intro: [
      'wizard.recipients.intro',
      'Lägg till dem som ska svara. Varje leverantör får en egen utvärdering, och varje person får ett eget mejl för var och en.',
    ],
  },
  {
    label: ['wizard.steps.review', 'Granska och skicka'],
    heading: ['wizard.review.heading', 'Granska och skicka'],
    intro: [
      'wizard.review.intro',
      'Kontrollera uppgifterna. Mottagarna får ett mejl med länk för varje utvärdering direkt när du skickar.',
    ],
  },
];

const EvaluationWizard = () => {
  const [t] = useTranslation('evaluations');
  const history = useHistory();
  const location = useLocation();
  const [today] = useState(() => toIsoDay(new Date()));
  const [seed] = useState(() => readWizardSeed(location.state));
  const [initial] = useState(() => seedToState(seed, today));
  const [state, dispatch] = useReducer(wizardReducer, initial);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [templatesStatus, setTemplatesStatus] = useState<TemplatesStatus>('loading');
  const [migrationPending, setMigrationPending] = useState(false);
  const [tags, setTags] = useState<EvaluationTagDto[]>([]);
  const [tagsLoaded, setTagsLoaded] = useState(false);
  const idempotency = useRef<IdempotencyKey | null>(null);
  const sendingNow = useRef(false);
  const mounted = useRef(true);
  const data: WizardData = useMemo(() => ({ templates, tags, today }), [templates, tags, today]);
  const dataRef = useRef(data);
  dataRef.current = data;

  const guard = useLeaveGuard((seed !== null || isDirty(state, initial)) && !migrationPending);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    // The templates call doubles as the check for an organisation whose evaluations are not migrated yet
    serviceEvaluations
      .getTemplates()
      .then(found => {
        if (!mounted) return;
        setTemplates(found);
        setTemplatesStatus('loaded');
      })
      .catch(error => {
        reportUnexpected(error);
        if (!mounted) return;
        if (errorKeyOf(error) === EvaluationErrors.MigrationPending) setMigrationPending(true);
        setTemplatesStatus('failed');
      });
    serviceEvaluations
      .getTags()
      .then(found => {
        if (!mounted) return;
        setTags(found);
        setTagsLoaded(true);
      })
      .catch(error => {
        reportUnexpected(error);
        if (!mounted || hasGlobalNotice(error)) return;
        if (errorKeyOf(error) === EvaluationErrors.MigrationPending) return;
        NotificationManager.error(
          t('quick.loadFailed', 'Det gick inte att hämta allt till Ny utvärdering.'),
        );
      });
    const projectId = initial.projectToLoad;
    if (projectId) {
      serviceEvaluations
        .getWizardProjects({ id: projectId })
        .then(found => {
          if (!mounted) return;
          const project = found.find(item => item._id === projectId) ?? null;
          dispatch({
            type: 'projectLoaded',
            project,
            data: dataRef.current,
            preselectWinners: seed?.preselectWinners === true,
          });
        })
        .catch(error => {
          reportUnexpected(error);
          if (mounted) {
            dispatch({
              type: 'projectLoaded',
              project: null,
              data: dataRef.current,
              preselectWinners: false,
            });
          }
        });
    }
    return () => {
      mounted = false;
    };
  }, [initial.projectToLoad, seed, t]);

  const from = fromOf(location.state);
  // Every in-app opening pushes the wizard on top of from, so leaving pops it and Back skips it
  const leaveWizard = () => {
    if (from) history.goBack();
    else history.replace(lastEvaluationsUrl());
  };

  if (migrationPending) {
    return (
      <div className={styles.wizard}>
        <InfoBox
          description={t(
            'errors.migrationPending',
            'Utvärderingar förbereds. Försök igen om en stund.',
          )}
        />
        <div className={styles.footer}>
          <BtButton color="white" onClick={leaveWizard} tabIndex={0}>
            {t('wizard.cancel', 'Avbryt')}
          </BtButton>
        </div>
      </div>
    );
  }

  const { step } = state;
  const isLast = step === LAST_STEP;
  const missing = isLast ? missingAll(state, data) : missingFor(step, state, data);
  const sendDisabled = missing.length > 0 || state.sending;

  // A footer button keeps focus when the next one takes its place: a double-click or held Enter acts once
  const once = (action: () => void) => (event?: MouseEvent | KeyboardEvent) => {
    if (event && (event.detail > 1 || ('repeat' in event && event.repeat))) return;
    action();
  };

  const goTo = (index: number) => {
    if (!state.sending && index <= state.maxReached) dispatch({ type: 'goTo', step: index });
  };

  const goNext = () => {
    if (!missing.length) dispatch({ type: 'goTo', step: step + 1 });
  };

  const send = async (event?: MouseEvent | KeyboardEvent) => {
    // A double-click or held Enter on Fortsätt reaches Skicka once step 5 shows
    if (event && (event.detail > 1 || ('repeat' in event && event.repeat))) return;
    if (sendingNow.current || missingAll(state, data).length) return;
    const body = toWizardBody(state, data, t);
    idempotency.current = idempotencyKeyFor(idempotency.current, JSON.stringify(body));
    sendingNow.current = true;
    dispatch({ type: 'setSending', sending: true });
    try {
      const outcome = await serviceEvaluations.startWizard({
        ...body,
        idempotencyKey: idempotency.current.key,
      });
      const names = Object.fromEntries(state.suppliers.map(item => [item._id, item.name]));
      const toast = outcomeToast(t, outcome, 'invitation', names);
      NotificationManager[toast.kind](toast.message);
      if (!mounted.current) return;
      const created = outcome.requestIds;
      if (created.length) {
        // A replace, so Back goes to where the wizard was opened instead of a wizard that could send again
        guard.release();
        history.replace(
          created.length === 1
            ? evaluationsUrl(`/requests/${created[0]}`)
            : evaluationsUrl(`?batch=${outcome.batchId}`),
          from ? { from } : undefined,
        );
        return;
      }
      // A stored empty outcome would replay forever under the same key
      if (!outcome.inProgress) idempotency.current = null;
    } catch (error) {
      if (!isUnansweredSend(error)) idempotency.current = null;
      reportUnexpected(error);
      const message = evaluationErrorMessage(t, error);
      if (message) NotificationManager.error(message);
      if (!mounted.current) return;
    }
    sendingNow.current = false;
    dispatch({ type: 'setSending', sending: false });
  };

  const stepContent = () => {
    switch (step) {
      case 0:
        return (
          <ContextStep
            state={state}
            dispatch={dispatch}
            tagIds={effectiveTagIds(state, data)}
            tags={tags}
            tagsLoaded={tagsLoaded}
            onTagsChange={setTags}
          />
        );
      case 1:
        return (
          <SuppliersStep
            value={state.suppliers}
            onChange={suppliers => dispatch({ type: 'setSuppliers', suppliers })}
            project={state.contextChoice === 'project' ? state.project : null}
          />
        );
      case 2:
        return (
          <TemplateStep
            state={state}
            data={data}
            templatesStatus={templatesStatus}
            dispatch={dispatch}
          />
        );
      case 3:
        return <RecipientsStep state={state} dispatch={dispatch} today={today} />;
      default:
        return <ReviewStep state={state} data={data} dispatch={dispatch} />;
    }
  };

  const [headingKey, headingText] = STEPS[step].heading;
  const [introKey, introText] = STEPS[step].intro;
  const supplierCount = state.suppliers.length;

  // A disabled button gets no hover, so the tooltip hangs on a wrapper around it.
  const withMissingTooltip = (button: ReactElement) =>
    missing.length > 0 && !state.projectToLoad ? (
      <TooltipContainer
        renderReferenceComponent={(className, ref) => (
          <span className={className} ref={ref}>
            {button}
          </span>
        )}
      >
        <span data-missing-tooltip>
          <MissingLine
            lead={isLast ? 'send' : 'continue'}
            items={missing.map(item => missingText(t, item))}
          />
        </span>
      </TooltipContainer>
    ) : (
      button
    );

  return (
    <div className={styles.wizard}>
      <BtStepper
        steps={STEPS.map((item, index) => ({
          key: item.label[0],
          label: t(item.label[0], item.label[1]),
          complete: !(
            index === LAST_STEP ? missingAll(state, data) : missingFor(index, state, data)
          ).length,
        }))}
        current={step}
        maxReached={state.maxReached}
        onStepClick={goTo}
      />
      <section className={styles.card}>
        {state.notice === 'seeded' && <InfoBox description={seededText(t, state)} />}
        {state.notice === 'contextCleared' && (
          <InfoBox
            description={t(
              'wizard.context.cleared',
              'Projektuppgifterna rensades eftersom du bytte typ av projekt.',
            )}
          />
        )}
        {state.notice === 'projectUnavailable' && (
          <InfoBox
            description={t(
              'wizard.context.unavailable',
              'Projektet kunde inte hämtas. Välj projekt igen.',
            )}
          />
        )}
        <div>
          <h3 className={styles.heading}>{t(headingKey, headingText)}</h3>
          <p className={styles.intro}>{t(introKey, introText)}</p>
        </div>
        {stepContent()}
      </section>
      <div className={styles.footer}>
        {step === 0 ? (
          <BtButton color="white" onClick={once(leaveWizard)} tabIndex={0}>
            {t('wizard.cancel', 'Avbryt')}
          </BtButton>
        ) : (
          <BtButton
            color="white"
            onClick={once(() => goTo(step - 1))}
            disabled={state.sending}
            tabIndex={0}
          >
            {t('wizard.back', 'Tillbaka')}
          </BtButton>
        )}
        <div className={styles.footerMissing}>
          {state.projectToLoad && (
            <span className={styles.hint}>
              {t('wizard.context.projectLoading', 'Hämtar projektet…')}
            </span>
          )}
        </div>
        {withMissingTooltip(
          isLast ? (
            <BtButton
              onClick={send}
              disabled={sendDisabled}
              loaderShow={state.sending}
              tabIndex={0}
            >
              {supplierCount > 1
                ? t('wizard.sendMany', {
                    count: supplierCount,
                    defaultValue_one: 'Skicka {{count}} utvärdering',
                    defaultValue_other: 'Skicka {{count}} utvärderingar',
                  })
                : t('quick.send', 'Skicka')}
            </BtButton>
          ) : (
            <BtButton onClick={once(goNext)} disabled={missing.length > 0} tabIndex={0}>
              {t('wizard.continue', 'Fortsätt')}
            </BtButton>
          ),
        )}
      </div>
      {/* A leave held mid-send waits: on success the redirect wins, otherwise the modal asks */}
      {guard.pending && !state.sending && (
        <LeaveWizardModal onStay={guard.stay} onLeave={guard.leave} />
      )}
    </div>
  );
};

export default EvaluationWizard;
